import React, { useState, useEffect, useCallback } from 'react';
import { X, Cookie, Shield, Check } from 'lucide-react';

// Export function to show banner
let showCookieBannerFn = null;

export const showCookieBanner = () => {
  if (showCookieBannerFn) {
    showCookieBannerFn();
  }
};

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
  });

  // Use the same strict, versioned consent validation as the analytics loader.
  const loadPreferences = useCallback(() => {
    const saved = window.cookieConsentManager?.read();
    setPreferences({ necessary: true, analytics: saved?.analytics === true });
    setSaveError('');
  }, []);

  // Expose function to show banner
  useEffect(() => {
    showCookieBannerFn = () => {
      loadPreferences();
      setShowPreferences(false);
      setIsVisible(true);
    };
    return () => {
      showCookieBannerFn = null;
    };
  }, [loadPreferences]);

  useEffect(() => {
    loadPreferences();
    const consent = window.cookieConsentManager?.read();
    const onConsentChange = () => {
      loadPreferences();
      if (!window.cookieConsentManager?.read()) setIsVisible(true);
    };
    window.addEventListener('cookie-consent-change', onConsentChange);
    let timer;
    if (!consent) {
      timer = setTimeout(() => setIsVisible(true), 1000);
    }
    return () => {
      clearTimeout(timer);
      window.removeEventListener('cookie-consent-change', onConsentChange);
    };
  }, [loadPreferences]);

  const saveAndClose = useCallback((prefs) => {
    if (!window.cookieConsentManager?.save(prefs.analytics)) {
      setSaveError('Non riesco a salvare la scelta nel browser. Analytics resta disattivato in questa pagina; controlla le impostazioni di memorizzazione e riprova.');
      return;
    }
    setIsVisible(false);
    setShowPreferences(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    saveAndClose({
      necessary: true,
      analytics: true,
    });
  }, [saveAndClose]);

  const handleRejectAll = useCallback(() => {
    saveAndClose({
      necessary: true,
      analytics: false,
    });
  }, [saveAndClose]);

  const handleSavePreferences = useCallback(() => {
    saveAndClose(preferences);
  }, [saveAndClose, preferences]);

  // Custom Toggle Component
  const Toggle = ({ checked, onChange, label, disabled = false }) => (
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative w-14 h-8 rounded-full transition-all duration-300 flex-shrink-0 ${
        disabled 
          ? 'bg-green-500 cursor-not-allowed' 
          : checked 
            ? 'bg-[#d4883a]' 
            : 'bg-gray-300 hover:bg-gray-400'
      }`}
    >
      <span 
        className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md transition-all duration-300 flex items-center justify-center ${
          checked || disabled ? 'translate-x-6' : 'translate-x-0'
        }`}
      >
        {(checked || disabled) && <Check className="w-4 h-4 text-green-500" />}
      </span>
    </button>
  );

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[9998]" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {!showPreferences ? (
            // Main Banner
            <div className="p-5 sm:p-8">
              {/* Mobile: Icon on top, Desktop: Icon on left */}
              <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                {/* Icon - Centered on mobile, left-aligned on desktop */}
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#d4883a]/10 rounded-xl flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                  <Cookie className="w-5 h-5 sm:w-7 sm:h-7 text-[#d4883a]" />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-base sm:text-xl font-bold text-[#0b114e] mb-1.5 sm:mb-2 text-center sm:text-left">
                    Questo sito utilizza i cookie
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-5 text-center sm:text-left">
                    Usiamo strumenti necessari al funzionamento del sito. Solo se acconsenti,
                    attiviamo Google Analytics per le statistiche di navigazione. Puoi rifiutare
                    o cambiare la scelta dalla Cookie Policy in qualsiasi momento.
                  </p>
                  {saveError && <p role="alert" className="text-red-700 text-sm mb-4">{saveError}</p>}
                  
                  {/* Buttons - Mobile: compact stacked, Desktop: inline */}
                  <div className="flex flex-col gap-2.5 sm:flex-row sm:gap-3">
                    {/* First row on mobile - full width */}
                    <button
                      type="button"
                      onClick={handleAcceptAll}
                      className="w-full sm:w-auto px-5 py-2.5 sm:px-6 sm:py-3 bg-[#d4883a] text-white rounded-xl font-semibold text-sm hover:bg-[#c47830] transition-colors"
                    >
                      Accetta tutti
                    </button>
                    
                    {/* Second row on mobile - two buttons side by side */}
                    <div className="flex gap-2.5 sm:gap-3 sm:contents">
                      <button
                        type="button"
                        onClick={handleRejectAll}
                        className="flex-1 sm:flex-none px-3 sm:px-6 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold text-xs sm:text-sm hover:bg-gray-200 transition-colors"
                      >
                        Rifiuta non necessari
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPreferences(true)}
                        className="flex-1 sm:flex-none px-3 sm:px-6 py-2.5 sm:py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold text-xs sm:text-sm hover:bg-gray-50 transition-colors"
                      >
                        Personalizza
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-3 sm:mt-4 flex justify-center sm:justify-start gap-4 text-xs text-gray-500">
                    <a href="/privacy-policy" className="hover:text-[#d4883a] transition-colors">
                      Privacy Policy
                    </a>
                    <a href="/cookie-policy" className="hover:text-[#d4883a] transition-colors">
                      Cookie Policy
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Preferences Panel
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6 text-[#0b114e]" />
                  <h3 className="text-lg font-bold text-[#0b114e]">
                    Preferenze Cookie
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreferences(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-4">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-[#0b114e]">Cookie Necessari</span>
                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          Sempre attivi
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Essenziali per il funzionamento del sito.
                      </p>
                    </div>
                    <Toggle label="Strumenti necessari" checked={true} onChange={() => {}} disabled={true} />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-4">
                      <span className="font-semibold text-[#0b114e] block mb-1">Cookie Analitici</span>
                      <p className="text-sm text-gray-600">
                        Google Analytics misura le visite solo dopo il tuo consenso.
                        Disattivandolo revochi la scelta; se è già caricato, la pagina si ricarica.
                      </p>
                    </div>
                    <Toggle 
                      label="Google Analytics"
                      checked={preferences.analytics} 
                      onChange={(val) => setPreferences(prev => ({ ...prev, analytics: val }))} 
                    />
                  </div>
                </div>

              </div>
              {saveError && <p role="alert" className="text-red-700 text-sm mb-4">{saveError}</p>}

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-200 transition-colors"
                >
                  Salva preferenze
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="flex-1 sm:flex-none px-6 py-3 bg-[#d4883a] text-white rounded-xl font-semibold text-sm hover:bg-[#c47830] transition-colors"
                >
                  Accetta tutti
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
